import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type {
    CambioCuentaCliente,
    Canal,
    CodigoPedido,
    Compromiso,
    CuentaCliente,
    IdiomaCuenta,
    MiPerfil,
    OperacionesClienteFinal,
    PropuestaCorreccion,
    SolicitudNueva,
    SolicitudPrivacidad,
    TokenCliente,
} from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Deshacer } from '../componentes/Deshacer';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { Interruptor } from '../componentes/Interruptor';
import { Ventana } from '../componentes/Ventana';
import { useEsMovil } from '../marco/tamano';
import { useEnvioRetrasado } from './envioRetrasado';
import { Identificarse } from './Identificarse';
import {
    diaYMes,
    emailValido,
    errorDeCodigo,
    esCodigo,
    etiquetaCanal,
    fechaCorta,
    fechaLarga,
    inicial,
    nombreCanal,
    telefonoLegible,
    telefonoValido,
    textoDeError,
    type ErrorCodigo,
} from './logica';
import { AvisoPc, Baldosa, BotonPc, EnlaceLegal, Pagina } from './Pagina';
import { PasoCodigo } from './PasoCodigo';
import { A_VALIDAR, D, T, VERSION_AVISO } from './textos';

/**
 * 2.10 «Mis datos» (con el token de cliente): su Cuenta Reservaria (nombre, teléfono, email e idioma; teléfono y email
 * solo con código), los negocios donde tiene perfil (canal preferido, permisos y alergias guardadas, G192), las
 * correcciones que le proponen (T23), sus solicitudes (T24, con «Programada», «Bloqueada por obligación legal» y
 * «Rechazada» con el motivo y la AEPD, G196, G199), pedir copia, corrección o borrado, unir cuentas y borrar la cuenta.
 *
 * - «Deshacer» 5 s (G240) en nombre, idioma, canal, permisos y correcciones: el cambio no se envía hasta que pasan.
 * - Antes de borrar, unir o cambiar el teléfono, `401 recent_verification_required` lleva a «Confirma que eres tú» (T31)
 *   y, con el código nuevo, de vuelta a la ventana.
 * - `401 unauthenticated` → «Tu sesión ha caducado» (lo hace la zona con `onCaducada`).
 */
export interface PropsMisDatos {
    ops: OperacionesClienteFinal;
    captcha: (accion: string) => Promise<string | null>;
    onToken: (token: TokenCliente) => void;
    onCaducada: () => void;
    onSalir: () => void;
    onCuentaBorrada: () => void;
    esperaDeshacer?: number;
    ahora?: () => number;
}

/** Idiomas de «Mis datos» (`CustomerLocale`, G363): los de las plantillas de servicio, cada uno en su idioma. */
const IDIOMAS: readonly (readonly [IdiomaCuenta, string])[] = [
    ['es', 'Español'],
    ['en', 'English'],
];

type Para = 'borrar' | 'telefono' | 'unir';
type Ambito = { perfil: MiPerfil } | { perfil: null };

type Vista =
    | { v: 'principal' }
    | { v: 'contacto'; kind: 'phone' | 'email'; valor: string; error: string | null }
    | { v: 'codigo'; kind: 'phone' | 'email'; valor: string; pedido: CodigoPedido; desde: number }
    | { v: 'conflicto'; verificacion: string; telefono: string }
    | { v: 'reauth'; para: Para; luego: () => void };

type VentanaAbierta =
    | { w: 'nombre'; valor: string; error: string | null }
    | { w: 'quitar-email' }
    | { w: 'copia'; ambito: Ambito }
    | { w: 'correccion'; perfil: MiPerfil; valor: string; error: string | null }
    | { w: 'borrar'; ambito: Ambito }
    | { w: 'compromisos'; ambito: Ambito; lista: Compromiso[] }
    | { w: 'unir'; verificacion: string; telefono: string }
    | { w: 'otros'; perfil: MiPerfil }
    | { w: 'alergias'; perfil: MiPerfil };

interface Datos {
    cuenta: CuentaCliente;
    version: string | null;
    perfiles: MiPerfil[];
    propuestas: PropuestaCorreccion[];
    solicitudes: SolicitudPrivacidad[];
}

const ESTADO_SOLICITUD: Record<SolicitudPrivacidad['status'], EstadoChip | null> = {
    received: 'pending',
    scheduled: 'pending',
    in_progress: 'pending',
    extended: 'pending',
    resolved: null,
    blocked: 'neutral',
    rejected: 'problem',
};
const ICONO_SOLICITUD: Record<SolicitudPrivacidad['type'], NombreIcono> = { access: 'download', rectification: 'pencil', erasure: 'trash-2' };

/** G511: cuántos pedidos de entradas futuros se cancelarán sin reembolso (los compromisos de Eventos y Entradas). */
const pedidosFuturos = (lista: readonly Compromiso[]): number =>
    lista.filter((c) => c.module === 'eventos-entradas' && c.kind === 'entradas').reduce((n, c) => n + c.count, 0);

const permisoComercial = (p: MiPerfil, c: Canal) => p.consents.find((x) => x.channel === c && x.purpose === 'marketing') ?? { channel: c, purpose: 'marketing' as const, state: 'none' as const, since: null };
const canalesComerciales = (p: MiPerfil): Canal[] => {
    const lista = p.available_channels ?? [];
    return lista.length > 0 ? lista : [...new Set(p.consents.filter((x) => x.purpose === 'marketing').map((x) => x.channel))];
};

/** La cuenta (con su `ETag`), sus negocios, las correcciones abiertas y sus solicitudes. */
async function leer(ops: OperacionesClienteFinal): Promise<Datos> {
    const cuentaLeida = await ops.getMyCustomerAccount();
    // Sin teléfono y con Canales (`phone_required`, G366): lo demás responde 409 hasta que lo dé; solo se ve la cuenta.
    if (cuentaLeida.datos.phone_required) {
        return { cuenta: cuentaLeida.datos, version: cuentaLeida.version, perfiles: [], propuestas: [], solicitudes: [] };
    }
    const [cuenta, perfiles, propuestas, solicitudes] = await Promise.all([
        Promise.resolve(cuentaLeida),
        ops.listMyBusinessProfiles(),
        ops.listMyCorrectionProposals('sent'),
        ops.listMyPrivacyRequests(),
    ]);
    return { cuenta: cuenta.datos, version: cuenta.version, perfiles: perfiles.data, propuestas: propuestas.data, solicitudes: solicitudes.data };
}

export function MisDatos({ ops, captcha, onToken, onCaducada, onSalir, onCuentaBorrada, esperaDeshacer = 5000, ahora = Date.now }: PropsMisDatos) {
    const movil = useEsMovil();
    const [carga, setCarga] = useState<'cargando' | 'error' | 'ok'>('cargando');
    const [datos, setDatos] = useState<Datos | null>(null);
    const [vista, setVista] = useState<Vista>({ v: 'principal' });
    const [ventana, setVentana] = useState<VentanaAbierta | null>(null);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [errorVentana, setErrorVentana] = useState<string | null>(null);
    const [aviso, setAviso] = useState('');
    const [codigo, setCodigo] = useState('');
    const [errorCodigo, setErrorCodigo] = useState<ErrorCodigo | null>(null);
    const [otroEnviado, setOtroEnviado] = useState(false);
    const envio = useEnvioRetrasado(esperaDeshacer);

    /** `401 unauthenticated`: la sesión de 30 minutos ha caducado. */
    const caducada = useCallback(
        (e: unknown): boolean => {
            if (esCodigo(e, 'unauthenticated')) {
                onCaducada();
                return true;
            }
            return false;
        },
        [onCaducada],
    );

    const recargar = useCallback(() => {
        leer(ops)
            .then((d) => {
                setDatos(d);
                setCarga('ok');
            })
            .catch((e: unknown) => {
                if (!caducada(e)) setCarga('error');
            });
    }, [ops, caducada]);

    useEffect(() => {
        let vivo = true;
        leer(ops)
            .then((d) => {
                if (!vivo) return;
                setDatos(d);
                setCarga('ok');
            })
            .catch((e: unknown) => {
                if (vivo && !caducada(e)) setCarga('error');
            });
        return () => {
            vivo = false;
        };
    }, [ops, caducada]);

    const cambiar = (f: (d: Datos) => Datos) => {
        setDatos((d) => (d === null ? d : f(d)));
    };
    const ponPerfil = (p: MiPerfil) => {
        cambiar((d) => ({ ...d, perfiles: d.perfiles.map((x) => (x.customer_id === p.customer_id ? p : x)) }));
    };
    const cerrar = () => {
        if (!ocupado) {
            setVentana(null);
            setErrorVentana(null);
        }
    };

    // ——— Cambios con «Deshacer» (G240): se ven al momento y se envían a los 5 s ———

    const guardarCuenta = (clave: 'nombre' | 'idioma', cambio: CambioCuentaCliente, antes: CuentaCliente) => {
        cambiar((d) => ({ ...d, cuenta: { ...d.cuenta, ...cambio } }));
        envio.programar(
            clave,
            async () => {
                try {
                    const r = await ops.updateMyCustomerAccount(cambio, datos?.version ?? '');
                    cambiar((d) => ({ ...d, cuenta: r.datos, version: r.version ?? d.version }));
                    setAviso(clave === 'nombre' ? D.hecho.nombre : D.hecho.idioma);
                } catch (e) {
                    if (caducada(e)) return;
                    cambiar((d) => ({ ...d, cuenta: antes }));
                    setAviso(textoDeError(e));
                    if (esCodigo(e, 'version_conflict')) recargar();
                }
            },
            () => {
                cambiar((d) => ({ ...d, cuenta: antes }));
            },
        );
    };

    const elegirCanal = (p: MiPerfil, canal: Canal) => {
        ponPerfil({ ...p, preferred_channel: canal });
        envio.programar(
            `canal-${p.customer_id}`,
            async () => {
                try {
                    ponPerfil(await ops.setMyPreferredChannel(p.customer_id, canal));
                    setAviso(D.hecho.canal);
                } catch (e) {
                    if (caducada(e)) return;
                    ponPerfil(p);
                    setAviso(textoDeError(e));
                }
            },
            () => {
                ponPerfil(p);
            },
        );
    };

    const cambiarPermiso = (p: MiPerfil, canal: Canal, dar: boolean) => {
        const otros = p.consents.filter((x) => !(x.channel === canal && x.purpose === 'marketing'));
        ponPerfil({ ...p, consents: [...otros, { channel: canal, purpose: 'marketing', state: dar ? 'granted' : 'withdrawn', since: new Date(ahora()).toISOString() }] });
        envio.programar(
            `permiso-${p.customer_id}-${canal}`,
            async () => {
                try {
                    ponPerfil(await ops.recordMyConsent(p.customer_id, { channel: canal, purpose: 'marketing', granted: dar, notice_version: VERSION_AVISO }));
                    setAviso(dar ? D.hecho.permisoDado : D.hecho.permisoRetirado);
                } catch (e) {
                    if (caducada(e)) return;
                    ponPerfil(p);
                    setAviso(textoDeError(e));
                }
            },
            () => {
                ponPerfil(p);
            },
        );
    };

    const decidir = (pr: PropuestaCorreccion, decision: 'accept' | 'reject') => {
        envio.programar(`propuesta-${pr.id}`, async () => {
            try {
                await ops.decideCorrectionProposal(pr.id, decision);
                cambiar((d) => ({ ...d, propuestas: d.propuestas.filter((x) => x.id !== pr.id) }));
                setAviso(decision === 'accept' ? D.hecho.aceptada : D.hecho.rechazada);
                if (decision === 'accept') recargar();
            } catch (e) {
                if (caducada(e)) return;
                setAviso(textoDeError(e));
            }
        });
    };

    // ——— Lo que va con ventana y sin «Deshacer» ———

    /** «Confirma que eres tú» (T31) y, con el código nuevo, `luego` (de vuelta a la ventana). */
    const pedirCodigoReciente = (para: Para, luego: () => void) => {
        setVentana(null);
        setErrorVentana(null);
        setVista({ v: 'reauth', para, luego });
    };

    const solicitar = async (datosSolicitud: SolicitudNueva, ambito: Ambito) => {
        setOcupado(true);
        setErrorVentana(null);
        try {
            const s = await ops.createMyPrivacyRequest(datosSolicitud);
            setOcupado(false);
            setVentana(null);
            if (datosSolicitud.type === 'erasure' && datosSolicitud.scope === 'account' && s.status !== 'scheduled') {
                onCuentaBorrada();
                return;
            }
            cambiar((d) => ({ ...d, solicitudes: [s, ...d.solicitudes.filter((x) => x.id !== s.id)] }));
            setAviso(
                datosSolicitud.type === 'access' ? D.pedido.copia : datosSolicitud.type === 'rectification' ? D.pedido.correccion : s.status === 'scheduled' ? D.pedido.programado : D.pedido.borrado,
            );
            if (datosSolicitud.type === 'erasure') recargar();
        } catch (e) {
            setOcupado(false);
            if (caducada(e)) return;
            if (esCodigo(e, 'recent_verification_required')) {
                pedirCodigoReciente('borrar', () => {
                    setVentana({ w: 'borrar', ambito });
                });
                return;
            }
            if (esCodigo(e, 'commitments_pending')) {
                const lista = ((e as ErrorApi).problema as { commitments?: Compromiso[] }).commitments ?? [];
                setVentana({ w: 'compromisos', ambito, lista });
                return;
            }
            setErrorVentana(textoDeError(e));
        }
    };

    const ambitoDe = (a: Ambito): Pick<SolicitudNueva, 'scope' | 'customer_id'> => (a.perfil === null ? { scope: 'account' } : { scope: 'business', customer_id: a.perfil.customer_id });

    const unir = async (verificacion: string, telefono: string) => {
        setOcupado(true);
        setErrorVentana(null);
        try {
            const cuenta = await ops.mergeMyCustomerAccounts(verificacion);
            setOcupado(false);
            setVentana(null);
            setVista({ v: 'principal' });
            cambiar((d) => ({ ...d, cuenta }));
            setAviso(D.cuentasUnidas);
            recargar();
        } catch (e) {
            setOcupado(false);
            if (caducada(e)) return;
            if (esCodigo(e, 'recent_verification_required')) {
                pedirCodigoReciente('unir', () => {
                    setVista({ v: 'conflicto', verificacion, telefono });
                    setVentana({ w: 'unir', verificacion, telefono });
                });
                return;
            }
            setErrorVentana(textoDeError(e));
        }
    };

    const quitarEmail = async () => {
        setOcupado(true);
        setErrorVentana(null);
        try {
            // Sin código: la respuesta ya es la cuenta sin email (G354).
            await ops.startContactChange({ kind: 'email', value: '' });
            setOcupado(false);
            setVentana(null);
            setVista({ v: 'principal' });
            setAviso(D.emailQuitado);
            recargar();
        } catch (e) {
            setOcupado(false);
            if (!caducada(e)) setErrorVentana(textoDeError(e));
        }
    };

    const quitarAlergias = async (p: MiPerfil) => {
        setOcupado(true);
        setErrorVentana(null);
        try {
            await ops.deleteMyHealthInfo(p.customer_id);
            setOcupado(false);
            setVentana(null);
            ponPerfil({ ...p, health_info: null });
        } catch (e) {
            setOcupado(false);
            if (!caducada(e)) setErrorVentana(textoDeError(e));
        }
    };

    // ——— Cambiar el teléfono o el email (con código) ———

    const enviarCodigoContacto = async (kind: 'phone' | 'email', valor: string, reenvio: boolean) => {
        if (!reenvio && (kind === 'phone' ? !telefonoValido(valor) : !emailValido(valor))) {
            setVista({ v: 'contacto', kind, valor, error: kind === 'phone' ? D.escribeTelefono : D.escribeEmail });
            return;
        }
        setOcupado(true);
        try {
            const pedido = await ops.startContactChange({ kind, value: valor.trim(), channel: kind === 'phone' ? 'whatsapp' : 'email' });
            setOcupado(false);
            if (pedido === undefined || !('verification_id' in pedido)) {
                // Sin código (quitar el email, G354): la respuesta ya es la cuenta.
                recargar();
                setVista({ v: 'principal' });
                return;
            }
            setCodigo('');
            setErrorCodigo(null);
            setOtroEnviado(reenvio);
            setVista({ v: 'codigo', kind, valor, pedido, desde: ahora() + (pedido.resend_after_seconds ?? 0) * 1000 });
        } catch (e) {
            setOcupado(false);
            if (caducada(e)) return;
            if (esCodigo(e, 'recent_verification_required')) {
                pedirCodigoReciente('telefono', () => {
                    setVista({ v: 'contacto', kind, valor, error: null });
                });
                return;
            }
            const texto = esCodigo(e, 'validation_failed') ? (kind === 'phone' ? D.escribeTelefono : D.escribeEmail) : textoDeError(e);
            if (reenvio) setErrorCodigo({ tipo: 'aviso', texto });
            else setVista({ v: 'contacto', kind, valor, error: texto });
        }
    };

    const confirmarContacto = async (kind: 'phone' | 'email', valor: string, pedido: CodigoPedido, c: string) => {
        if (c.length !== 6) {
            setErrorCodigo({ tipo: 'incorrecto', quedan: 3 });
            return;
        }
        setOcupado(true);
        try {
            const cuenta = await ops.confirmContactChange(pedido.verification_id, c);
            setOcupado(false);
            cambiar((d) => ({ ...d, cuenta }));
            setVista({ v: 'principal' });
            setAviso(kind === 'phone' ? D.telefonoCambiado : D.emailCambiado);
            recargar();
        } catch (e) {
            setOcupado(false);
            if (caducada(e)) return;
            const malo = errorDeCodigo(e);
            if (malo !== null) {
                setErrorCodigo(malo);
                if (malo.tipo === 'incorrecto') setCodigo('');
                return;
            }
            if (esCodigo(e, 'conflict') && kind === 'phone') {
                // El teléfono es de otra Cuenta Reservaria: se pueden unir con esta misma verificación (T34).
                setVista({ v: 'conflicto', verificacion: pedido.verification_id, telefono: valor.trim() });
                return;
            }
            setErrorCodigo({ tipo: 'aviso', texto: textoDeError(e) });
        }
    };

    // ——— Pantalla ———

    const principal = vista.v === 'principal';
    const volver = principal ? undefined : { texto: D.misDatos, accion: () => { setVista({ v: 'principal' }); } };
    const pagina = (contenido: ReactNode, conTitulo = false) => (
        <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} volver={volver} titulo={conTitulo ? { titulo: T.t15.titulo, sub: T.t15.sub } : undefined}>
            {contenido}
            <span role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                {aviso}
            </span>
        </Pagina>
    );

    if (carga === 'cargando' || datos === null) {
        if (carga === 'error') {
            return pagina(<ErrorDeCarga onReintentar={() => { setCarga('cargando'); recargar(); }} />);
        }
        return pagina(
            <section className="rv-pc-card" aria-busy="true" aria-label={D.cargando}>
                {[0, 1, 2].map((i) => (
                    <span key={i} className="rv-pc-esqueleto" style={{ height: i === 0 ? 26 : 18, width: i === 0 ? '60%' : '90%' }} />
                ))}
            </section>,
            true,
        );
    }
    if (carga === 'error') {
        return pagina(<ErrorDeCarga onReintentar={() => { setCarga('cargando'); recargar(); }} />);
    }

    const { cuenta, perfiles, propuestas, solicitudes } = datos;
    // Una cuenta nacida solo con el email (G365) aún no tiene teléfono: la fila sale vacía, como un email sin poner.
    const telefono = cuenta.phone === null ? '' : telefonoLegible(cuenta.phone);

    if (vista.v === 'reauth') {
        return pagina(
            <Identificarse
                ops={ops}
                contexto={{ tipo: 'reauth', para: vista.para, telefono: cuenta.phone, email: cuenta.email ?? null }}
                captcha={captcha}
                ahora={ahora}
                onToken={(t) => {
                    onToken(t);
                    setVista({ v: 'principal' });
                    vista.luego();
                }}
            />,
        );
    }

    // «Añade tu teléfono» (G366): sin teléfono no se pasa de aquí; solo se puede dar o cerrar la sesión.
    const obligado = cuenta.phone_required;
    if (vista.v === 'contacto' || (obligado && vista.v === 'principal')) {
        const cv = vista.v === 'contacto' ? vista : { v: 'contacto' as const, kind: 'phone' as const, valor: '', error: null };
        const tel = cv.kind === 'phone';
        const textos = obligado && tel ? { titulo: A_VALIDAR.anadeTelefono.titulo, sub: A_VALIDAR.anadeTelefono.sub, campo: A_VALIDAR.anadeTelefono.campo } : tel ? D.nuevoTelefono : D.nuevoEmail;
        return pagina(
            <section className="rv-pc-card" aria-labelledby="rv-pc-contacto-t">
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-contacto-t">{textos.titulo}</h2>
                    <span>{textos.sub}</span>
                </div>
                <Campo
                    etiqueta={textos.campo}
                    tipo={tel ? 'tel' : 'email'}
                    valor={cv.valor}
                    onChange={(v) => { setVista({ ...cv, valor: v, error: null }); }}
                    max={tel ? 20 : 254}
                    error={cv.error}
                    ayuda={tel ? T.t5.telefono : null}
                    onEnter={() => void enviarCodigoContacto(cv.kind, cv.valor, false)}
                />
                <div className="rv-pc-acciones">
                    <BotonPc texto={D.enviarCodigo} icono="send" ocupado={ocupado} ocupadoTexto={D.enviando} onClick={() => void enviarCodigoContacto(cv.kind, cv.valor, false)} />
                    {obligado && tel && (
                        <button type="button" className="rv-pc-l rv-pc-l--centro" onClick={onSalir}>
                            <Icono nombre="log-out" />
                            <span>{A_VALIDAR.anadeTelefono.cerrarSesion}</span>
                        </button>
                    )}
                    {!tel && cuenta.email != null && cuenta.email !== '' && (
                        <button type="button" className="rv-pc-l rv-pc-l--centro" onClick={() => { setVentana({ w: 'quitar-email' }); }}>
                            <Icono nombre="trash-2" />
                            <span>{D.nuevoEmail.quitar}</span>
                        </button>
                    )}
                </div>
            </section>,
        );
    }

    if (vista.v === 'codigo') {
        const tel = vista.kind === 'phone';
        return pagina(
            <PasoCodigo
                destino={tel ? T.t7.whatsapp(vista.valor.trim()) : T.t7.email(vista.valor.trim())}
                cambiar={{ texto: tel ? D.cambiarTelefono : D.cambiarEmail, accion: () => { setVista({ v: 'contacto', kind: vista.kind, valor: vista.valor, error: null }); } }}
                expira={vista.pedido.expires_at}
                reenviarDesde={vista.desde}
                codigo={codigo}
                onCodigo={(c) => {
                    setCodigo(c);
                    if (errorCodigo?.tipo === 'incorrecto' || errorCodigo?.tipo === 'aviso') setErrorCodigo(null);
                    setOtroEnviado(false);
                }}
                error={errorCodigo}
                otroEnviado={otroEnviado}
                ocupado={ocupado}
                onComprobar={(c) => void confirmarContacto(vista.kind, vista.valor, vista.pedido, c)}
                onReenviar={() => void enviarCodigoContacto(vista.kind, vista.valor, true)}
                ahora={ahora}
            />,
        );
    }

    if (vista.v === 'conflicto') {
        return pagina(
            <>
                <section className="rv-pc-card" aria-labelledby="rv-pc-conflicto-t">
                    <Baldosa icono="git-compare" fondo="var(--rv-ink)" color="#fff" />
                    <div className="rv-pc-card__cab">
                        <h2 id="rv-pc-conflicto-t">{T.t34.titulo}</h2>
                        <span>{T.t34.sub(vista.telefono)}</span>
                    </div>
                    <div className="rv-pc-acciones">
                        <BotonPc texto={T.t34.unir} icono="git-compare" onClick={() => { setVentana({ w: 'unir', verificacion: vista.verificacion, telefono: vista.telefono }); }} />
                        <BotonPc tipo="sec" texto={T.t34.ahoraNo} icono="arrow-left" onClick={() => { setVista({ v: 'principal' }); }} />
                    </div>
                </section>
                {ventanas()}
            </>,
        );
    }

    function ventanas() {
        const v = ventana;
        const abierta = (w: VentanaAbierta['w']) => v?.w === w;
        const comun = { movil, barraInferior: false, onCerrar: cerrar, ocupado };
        const nombreDe = (a: Ambito) => (a.perfil === null ? null : a.perfil.business.name);
        return (
            <>
                <Ventana
                    {...comun}
                    abierta={abierta('nombre')}
                    titulo={T.t17.titulo}
                    texto={T.t17.sub}
                    accion={D.guardar}
                    onAccion={() => {
                        if (v?.w !== 'nombre') return;
                        const nuevo = v.valor.trim();
                        if (nuevo === '') {
                            setVentana({ ...v, error: D.escribeNombre });
                            return;
                        }
                        setVentana(null);
                        if (nuevo !== cuenta.display_name) guardarCuenta('nombre', { display_name: nuevo }, cuenta);
                    }}
                >
                    {v?.w === 'nombre' && (
                        <Campo etiqueta={T.t9.nombre} valor={v.valor} onChange={(x) => { setVentana({ ...v, valor: x, error: null }); }} autocompletar="name" mayusculas="words" max={120} error={v.error} autoFocus />
                    )}
                </Ventana>
                <Ventana {...comun} abierta={abierta('quitar-email')} titulo={T.t18.titulo} texto={T.t18.sub} accion={D.quitarEmail} accionIcono="trash-2" ocupadoTexto={D.guardando} onAccion={() => void quitarEmail()}>
                    {errorVentana !== null && v?.w === 'quitar-email' && <AvisoPc texto={errorVentana} />}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('copia')}
                    titulo={v?.w === 'copia' ? (v.ambito.perfil === null ? T.t29.titulo : T.t25.titulo(v.ambito.perfil.business.name)) : ''}
                    texto={v?.w === 'copia' ? (v.ambito.perfil === null ? T.t29.sub : T.t25.sub) : ''}
                    accion={D.pedirCopia}
                    accionIcono="download"
                    ocupadoTexto={D.pidiendo}
                    onAccion={() => {
                        if (v?.w === 'copia') void solicitar({ type: 'access', ...ambitoDe(v.ambito) }, v.ambito);
                    }}
                >
                    {errorVentana !== null && v?.w === 'copia' && <AvisoPc texto={errorVentana} />}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('correccion')}
                    titulo={v?.w === 'correccion' ? T.t26.titulo(v.perfil.business.name) : ''}
                    texto={T.t26.sub}
                    accion={D.enviar}
                    accionIcono="send"
                    ocupadoTexto={D.enviando}
                    onAccion={() => {
                        if (v?.w !== 'correccion') return;
                        if (v.valor.trim() === '') {
                            setVentana({ ...v, error: D.cuentale });
                            return;
                        }
                        void solicitar({ type: 'rectification', scope: 'business', customer_id: v.perfil.customer_id, details: v.valor.trim() }, { perfil: v.perfil });
                    }}
                >
                    {v?.w === 'correccion' && (
                        <>
                            <Campo etiqueta={T.t26.campo} valor={v.valor} onChange={(x) => { setVentana({ ...v, valor: x, error: null }); }} filas={3} max={2000} ayuda={T.t26.ayuda} error={v.error} />
                            {errorVentana !== null && <AvisoPc texto={errorVentana} />}
                        </>
                    )}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('borrar')}
                    titulo={v?.w === 'borrar' ? (v.ambito.perfil === null ? T.t30.titulo : T.t27.titulo(v.ambito.perfil.business.name)) : ''}
                    texto={v?.w === 'borrar' ? (v.ambito.perfil === null ? T.t30.sub : T.t27.sub(v.ambito.perfil.business.name)) : ''}
                    accion={v?.w === 'borrar' && v.ambito.perfil === null ? D.borrarMiCuenta : D.borrarMisDatos}
                    accionIcono="trash-2"
                    ocupadoTexto={D.borrandoTexto}
                    onAccion={() => {
                        if (v?.w === 'borrar') void solicitar({ type: 'erasure', ...ambitoDe(v.ambito) }, v.ambito);
                    }}
                >
                    {errorVentana !== null && v?.w === 'borrar' && <AvisoPc texto={errorVentana} />}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('compromisos')}
                    titulo={v?.w === 'compromisos' ? (v.ambito.perfil === null ? T.t32.tituloCuenta : T.t32.tituloNegocio(v.ambito.perfil.business.name)) : ''}
                    texto={T.t32.sub}
                    accion={T.t32.si}
                    accionIcono="trash-2"
                    cancelar={T.t32.no}
                    ocupadoTexto={D.borrandoTexto}
                    onAccion={() => {
                        if (v?.w === 'compromisos') void solicitar({ type: 'erasure', ...ambitoDe(v.ambito), confirm_cancellations: true }, v.ambito);
                    }}
                >
                    {v?.w === 'compromisos' && (
                        <div className="rv-pc-compromisos">
                            {v.lista.map((c) => (
                                <div key={`${c.module}-${c.kind}`} className="rv-pc-compromiso">
                                    <span>
                                        {(c.items ?? []).length === 0 ? (
                                            <strong>{c.kind}</strong>
                                        ) : (
                                            (c.items ?? []).map((it, i) => (
                                                <span key={`${it.id}${String(i)}`} style={{ display: 'block' }}>
                                                    {[it.date != null ? fechaLarga(it.date) : null, it.label].filter((x) => x != null && x !== '').join(' · ')}
                                                </span>
                                            ))
                                        )}
                                    </span>
                                </div>
                            ))}
                            {pedidosFuturos(v.lista) > 0 && (
                                <span className="rv-pc-nota">
                                    <Icono nombre="ticket" />
                                    <span>{T.t32.pedidosSinReembolso(pedidosFuturos(v.lista))}</span>
                                </span>
                            )}
                            <span className="rv-pc-nota">
                                <Icono nombre="scale" />
                                <span>
                                    {nombreDe(v.ambito) === null ? <span data-texto-a-validar="">{A_VALIDAR.devolucionCuenta}</span> : T.t32.devolucion(nombreDe(v.ambito) ?? '')} {T.t32.ley}
                                </span>
                            </span>
                            <BotonPc
                                tipo="sec"
                                texto={T.t32.cuandoTermine}
                                icono="calendar-clock"
                                desactivado={ocupado}
                                onClick={() => void solicitar({ type: 'erasure', ...ambitoDe(v.ambito), when: 'after_commitments' }, v.ambito)}
                            />
                            {errorVentana !== null && <AvisoPc texto={errorVentana} />}
                        </div>
                    )}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('unir')}
                    titulo={T.t35.titulo}
                    texto={v?.w === 'unir' ? T.t35.sub(v.telefono) : ''}
                    accion={T.t35.accion}
                    accionIcono="git-compare"
                    ocupadoTexto={D.uniendo}
                    onAccion={() => {
                        if (v?.w === 'unir') void unir(v.verificacion, v.telefono);
                    }}
                >
                    {errorVentana !== null && v?.w === 'unir' && <AvisoPc texto={errorVentana} />}
                </Ventana>
                <Ventana {...comun} abierta={abierta('otros')} titulo={A_VALIDAR.otrosDerechos.titulo} cancelar="" accion={A_VALIDAR.otrosDerechos.cerrar} onAccion={cerrar}>
                    {v?.w === 'otros' && (
                        <span className="rv-pc-info" data-texto-a-validar="">
                            <span>
                                {A_VALIDAR.otrosDerechos.sub(v.perfil.business.name)}{' '}
                                <EnlaceLegal url={v.perfil.business.privacy_url ?? null}>{T.privacidadDe(v.perfil.business.name)}</EnlaceLegal>
                            </span>
                        </span>
                    )}
                </Ventana>
                <Ventana
                    {...comun}
                    abierta={abierta('alergias')}
                    titulo={A_VALIDAR.quitarAlergias.titulo}
                    texto={v?.w === 'alergias' ? A_VALIDAR.quitarAlergias.sub(v.perfil.business.name) : ''}
                    accion={D.quitar}
                    accionIcono="trash-2"
                    ocupadoTexto={D.quitando}
                    onAccion={() => {
                        if (v?.w === 'alergias') void quitarAlergias(v.perfil);
                    }}
                >
                    {errorVentana !== null && v?.w === 'alergias' && <AvisoPc texto={errorVentana} />}
                </Ventana>
            </>
        );
    }

    const deshacer = (clave: string) => (envio.pendiente === clave ? <Deshacer onDeshacer={envio.deshacer} duracion={esperaDeshacer} clave={clave} /> : null);
    const idioma: IdiomaCuenta = cuenta.locale ?? 'es';

    return pagina(
        <>
            {propuestas.length > 0 && (
                <section className="rv-pc-card rv-pc-card--seccion" aria-labelledby="rv-pc-prop-t">
                    <div className="rv-pc-card__cab">
                        <h2 id="rv-pc-prop-t">{T.t23.titulo}</h2>
                        <span>{T.t23.sub}</span>
                    </div>
                    {propuestas.map((pr) => {
                        const negocio = pr.business?.name ?? '';
                        const ahoraValor = pr.field === 'display_name' ? cuenta.display_name : (cuenta.email ?? '');
                        return (
                            <div key={pr.id} className="rv-pc-propuesta">
                                <div className="rv-pc-propuesta__quien">
                                    <span aria-hidden="true" className="rv-pc__avatar">{inicial(negocio)}</span>
                                    <span>
                                        <b>{negocio}</b> {pr.field === 'display_name' ? T.t23.nombre : T.t23.email}
                                    </span>
                                </div>
                                <div className="rv-pc-propuesta__datos">
                                    <span>{T.t23.ahora}</span>
                                    <s>{ahoraValor}</s>
                                    <span>{T.t23.propuesto}</span>
                                    <strong>{pr.proposed_value}</strong>
                                </div>
                                <div className="rv-pc-propuesta__pie">
                                    <span>{D.caducaEl(diaYMes(pr.expires_at))}</span>
                                    {envio.pendiente === `propuesta-${pr.id}` ? (
                                        deshacer(`propuesta-${pr.id}`)
                                    ) : (
                                        <span className="rv-pc-propuesta__botones">
                                            <BotonPc tipo="sec" peq texto={T.t23.rechazar} icono="x" onClick={() => { decidir(pr, 'reject'); }} />
                                            <BotonPc peq texto={T.t23.aceptar} icono="check" onClick={() => { decidir(pr, 'accept'); }} />
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            <section className="rv-pc-card rv-pc-card--seccion" aria-labelledby="rv-pc-cuenta-t">
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-cuenta-t">{T.t16.titulo}</h2>
                    <span>{T.t16.sub}</span>
                </div>
                <Fila etiqueta={D.nombre} valor={cuenta.display_name}>
                    {deshacer('nombre') ?? <BotonPc tipo="sec" peq texto={D.cambiar} icono="pencil" onClick={() => { setVentana({ w: 'nombre', valor: cuenta.display_name, error: null }); }} />}
                </Fila>
                <Fila etiqueta={D.telefono} valor={telefono}>
                    <BotonPc tipo="sec" peq texto={D.cambiar} icono="pencil" onClick={() => { setVista({ v: 'contacto', kind: 'phone', valor: '', error: null }); }} />
                </Fila>
                <Fila
                    etiqueta={D.email}
                    valor={cuenta.email ?? D.sinEmail}
                    chip={cuenta.email != null && cuenta.email_verified === false ? <Chip estado="pending">{D.sinVerificar}</Chip> : undefined}
                >
                    <BotonPc
                        tipo="sec"
                        peq
                        texto={cuenta.email == null ? D.anadir : D.cambiar}
                        icono={cuenta.email == null ? 'plus' : 'pencil'}
                        onClick={() => { setVista({ v: 'contacto', kind: 'email', valor: '', error: null }); }}
                    />
                </Fila>
                <div className="rv-pc-fila">
                    <span className="rv-pc-fila__texto">
                        <span>{D.idioma}</span>
                    </span>
                    {deshacer('idioma') ?? (
                        <select
                            className="rv-pc-select"
                            aria-label={D.idioma}
                            value={idioma}
                            onChange={(e) => {
                                const elegido = IDIOMAS.find(([c]) => c === e.target.value)?.[0];
                                if (elegido !== undefined) guardarCuenta('idioma', { locale: elegido }, cuenta);
                            }}
                        >
                            {IDIOMAS.map(([c, n]) => (
                                <option key={c} value={c}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </section>

            <section className="rv-pc-card rv-pc-card--seccion" aria-labelledby="rv-pc-negocios-t">
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-negocios-t">{T.t19.titulo}</h2>
                    <span>{T.t19.sub}</span>
                </div>
                {perfiles.length === 0 && (
                    <div className="rv-pc-vacio">
                        <span aria-hidden="true">
                            <Icono nombre="store" />
                        </span>
                        <strong>{T.t19.vacioTitulo}</strong>
                        <small>{T.t19.vacioSub}</small>
                    </div>
                )}
                {perfiles.map((p) => (
                    <Negocio
                        key={p.customer_id}
                        perfil={p}
                        abierto={abierto === p.customer_id}
                        onAbrir={() => { setAbierto(abierto === p.customer_id ? null : p.customer_id); }}
                        deshacer={deshacer}
                        onCanal={(c) => { elegirCanal(p, c); }}
                        onPermiso={(c, dar) => { cambiarPermiso(p, c, dar); }}
                        onVentana={(w) => {
                            setErrorVentana(null);
                            setVentana(
                                w === 'copia'
                                    ? { w, ambito: { perfil: p } }
                                    : w === 'borrar'
                                      ? { w, ambito: { perfil: p } }
                                      : w === 'correccion'
                                        ? { w, perfil: p, valor: '', error: null }
                                        : { w, perfil: p },
                            );
                        }}
                    />
                ))}
            </section>

            {solicitudes.length > 0 && (
                <section className="rv-pc-card rv-pc-card--seccion" aria-labelledby="rv-pc-sol-t">
                    <div className="rv-pc-card__cab">
                        <h2 id="rv-pc-sol-t">{T.t24.titulo}</h2>
                        <span>{T.t24.sub}</span>
                    </div>
                    {solicitudes.map((s) => {
                        const chip = ESTADO_SOLICITUD[s.status];
                        const plazo = s.extended_due_at ?? s.due_at;
                        return (
                            <div key={s.id} className="rv-pc-solicitud">
                                <span aria-hidden="true">
                                    <Icono nombre={ICONO_SOLICITUD[s.type]} />
                                </span>
                                <span>
                                    <strong>{`${T.t24.tipo[s.type]} · ${s.business?.name ?? T.t24.todaLaCuenta}`}</strong>
                                    <span>
                                        {D.pedida(diaYMes(s.received_at))}
                                        {s.status === 'resolved' && s.resolved_at != null ? ` · ${T.t24.estado.resolved} el ${diaYMes(s.resolved_at)}` : s.status === 'rejected' || s.status === 'blocked' ? '' : ` · ${D.respuestaAntes(diaYMes(plazo))}`}
                                    </span>
                                    {s.status === 'rejected' && (
                                        <>
                                            {s.rejection_reason != null && s.rejection_reason !== '' && <span>{s.rejection_reason}</span>}
                                            <span>{T.t24.aepd}</span>
                                        </>
                                    )}
                                </span>
                                {chip !== null && <Chip estado={chip}>{T.t24.estado[s.status]}</Chip>}
                            </div>
                        );
                    })}
                </section>
            )}

            <section className="rv-pc-card rv-pc-card--seccion" aria-labelledby="rv-pc-toda-t">
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-toda-t">{T.t28.titulo}</h2>
                    <span>{T.t28.sub}</span>
                </div>
                <div className="rv-pc-botones">
                    <BotonPc tipo="sec" peq texto={T.t28.copia} icono="download" onClick={() => { setErrorVentana(null); setVentana({ w: 'copia', ambito: { perfil: null } }); }} />
                    <BotonPc tipo="sec" peq rojo texto={T.t28.borrar} icono="trash-2" onClick={() => { setErrorVentana(null); setVentana({ w: 'borrar', ambito: { perfil: null } }); }} />
                </div>
            </section>
            {ventanas()}
        </>,
        true,
    );
}

function Fila({ etiqueta, valor, chip, children }: { etiqueta: string; valor: string; chip?: ReactNode; children: ReactNode }) {
    return (
        <div className="rv-pc-fila">
            <span className="rv-pc-fila__texto">
                <span>{etiqueta}</span>
                <strong>
                    <span>{valor}</span>
                    {chip}
                </strong>
            </span>
            {children}
        </div>
    );
}

function ErrorDeCarga({ onReintentar }: { onReintentar: () => void }) {
    return (
        <section className="rv-pc-card" aria-labelledby="rv-pc-error-t">
            <Baldosa icono="wifi-off" fondo="var(--rv-paper)" color="var(--rv-ink)" />
            <div className="rv-pc-card__cab">
                <h2 id="rv-pc-error-t">{D.errorCarga}</h2>
                <span>{D.errorCargaSub}</span>
            </div>
            <BotonPc texto={D.reintentar} icono="refresh-cw" onClick={onReintentar} />
        </section>
    );
}

type VentanaDeNegocio = 'copia' | 'correccion' | 'borrar' | 'otros' | 'alergias';

function Negocio({
    perfil: p,
    abierto,
    onAbrir,
    deshacer,
    onCanal,
    onPermiso,
    onVentana,
}: {
    perfil: MiPerfil;
    abierto: boolean;
    onAbrir: () => void;
    deshacer: (clave: string) => ReactNode;
    onCanal: (c: Canal) => void;
    onPermiso: (c: Canal, dar: boolean) => void;
    onVentana: (w: VentanaDeNegocio) => void;
}) {
    const canales = canalesComerciales(p);
    const conOfertas = canales.filter((c) => permisoComercial(p, c).state === 'granted').map(nombreCanal);
    const pendiente = canales.some((c) => permisoComercial(p, c).state === 'pending_confirmation');
    const preferido = p.preferred_channel ?? 'email';
    const opciones = [...new Set([...(p.available_channels ?? []), preferido])];
    const idCuerpo = `rv-pc-negocio-${p.customer_id}`;
    const nombre = p.business.name;
    return (
        <div className="rv-pc-negocio">
            <button type="button" className="rv-pc-negocio__cab" aria-expanded={abierto} aria-controls={idCuerpo} onClick={onAbrir}>
                <span aria-hidden="true" className="rv-pc__avatar">{inicial(nombre)}</span>
                <span>
                    <strong>{nombre}</strong>
                    <small>{`${D.confirmacionesPor(nombreCanal(preferido))} · ${conOfertas.length > 0 ? D.ofertasPor(conOfertas.join(' y ')) : D.sinOfertas}`}</small>
                </span>
                {pendiente && <Chip estado="pending">{D.pendiente}</Chip>}
                <Icono nombre="chevron-down" />
            </button>
            {abierto && (
                <div id={idCuerpo} className="rv-pc-negocio__cuerpo">
                    {p.ended != null && (
                        // TEXTO A VALIDAR (G242): aviso de relación terminada.
                        <p className="rv-pc-terminada" data-texto-a-validar="">
                            {A_VALIDAR.terminada[p.ended.reason](nombre, fechaCorta(p.ended.ended_at))}
                        </p>
                    )}
                    <label className="rv-pc-negocio__canal">
                        <span>{T.t20.porCanal}</span>
                        {deshacer(`canal-${p.customer_id}`) ?? (
                            <select
                                className="rv-pc-select rv-pc-select--ancho"
                                value={preferido}
                                onChange={(e) => { onCanal(e.target.value as Canal); }}
                            >
                                {opciones.map((c) => (
                                    <option key={c} value={c}>
                                        {etiquetaCanal(c)}
                                    </option>
                                ))}
                            </select>
                        )}
                        <span>{T.t20.respaldo}</span>
                    </label>
                    <Interruptor etiqueta={T.t20.siempre} ayuda={T.t20.siempreAyuda} valor bloqueado linea="arriba" ariaLabel={`${T.t20.siempre}: siempre activado`} />
                    {canales.map((c) => {
                        const permiso = permisoComercial(p, c);
                        const clave = `permiso-${p.customer_id}-${c}`;
                        const fecha = permiso.since != null ? fechaCorta(permiso.since) : null;
                        const ayuda =
                            permiso.state === 'granted'
                                ? fecha === null ? undefined : T.t21.si(fecha)
                                : permiso.state === 'withdrawn'
                                  ? fecha === null ? T.t21.no : T.t21.baja(fecha)
                                  : permiso.state === 'pending_confirmation'
                                    ? T.t21.activalo
                                    : T.t21.no;
                        return (
                            <div key={c}>
                                <Interruptor
                                    etiqueta={T.t21.etiqueta(nombreCanal(c))}
                                    ayuda={ayuda}
                                    chip={permiso.state === 'pending_confirmation' ? T.t21.pendiente : undefined}
                                    chipEstado="pending"
                                    valor={permiso.state === 'granted'}
                                    linea="arriba"
                                    onChange={(dar) => { onPermiso(c, dar); }}
                                />
                                {permiso.state === 'pending_confirmation' && fecha !== null && <p className="rv-pc-nota" style={{ margin: '-4px 0 8px' }}>{T.t21.apuntaron(nombre, fecha)}</p>}
                                {deshacer(clave)}
                            </div>
                        );
                    })}
                    {p.health_info != null && (
                        <div className="rv-pc-fila" style={{ borderTop: '1px solid var(--rv-rule)' }}>
                            <span className="rv-pc-fila__texto">
                                <span>{D.alergias}</span>
                                <strong>{p.health_info.text}</strong>
                                <small>{D.guardadoEl(fechaCorta(p.health_info.consented_at))}</small>
                            </span>
                            <BotonPc tipo="sec" peq rojo texto={D.quitar} icono="trash-2" onClick={() => { onVentana('alergias'); }} />
                        </div>
                    )}
                    <div className="rv-pc-negocio__acciones">
                        <BotonPc tipo="sec" peq texto={T.t22.copia} icono="download" onClick={() => { onVentana('copia'); }} />
                        <BotonPc tipo="sec" peq texto={T.t22.correccion} icono="pencil" onClick={() => { onVentana('correccion'); }} />
                        <BotonPc tipo="sec" peq rojo texto={T.t22.borrar} icono="trash-2" onClick={() => { onVentana('borrar'); }} />
                        <BotonPc tipo="sec" peq texto={T.t22.otros} icono="scale" onClick={() => { onVentana('otros'); }} />
                    </div>
                </div>
            )}
        </div>
    );
}
